// ---------------------------------------------------------------------------
// analytics.js — real, deterministic caseload analytics for AI Insights.
//
// This file is what "AI Insights" actually computes today. Everything here
// runs client-side, over the teacher's own students / goals / incidents /
// progress-entries — no mock data, no external LLM call, no marketing math.
//
// The optional LLM layer (in ./narrative.js) turns these facts into a
// plain-English paragraph or a bullet list of suggested interventions
// when the teacher clicks a "Get AI narrative" button. That call goes to
// the same shared Supabase Edge Function that drafts SMART goals, so it
// costs the teacher nothing and requires no per-user key.
//
// Why bother with deterministic signal at all? Two reasons:
//
//   1. Trust. Teachers don't want a hallucinated black box telling them a
//      student is at risk. They want to see the count of stalled goals,
//      the day-of-week histogram of incidents, and the linear regression
//      that says "at this rate, mastery is 42 days out." Then the AI
//      narrative is a summary of things the teacher can already see,
//      not a prediction pulled from nowhere.
//
//   2. Offline. The Supabase Edge Function may not be deployed yet, may
//      be rate-limited, or may cost money. The deterministic layer keeps
//      the page useful even when the LLM path is unavailable.
//
// Public API
// ----------
//
//   analyzeCaseload({students, goals, incidents, progressEntries, now}) → {
//     summary,          // caseload roll-ups (counts, trends)
//     studentInsights,  // per-student facts + signal + flags
//     patterns,         // caseload-wide patterns worth surfacing
//     recommendations,  // deterministic, evidence-backed CTAs
//     computedAt,       // ISO timestamp
//   }
//
// The output is intentionally UI-agnostic. All string values are already
// human-friendly (labels, not enum values). All numbers already have their
// units (percent, days, per-week velocity). The React components in
// src/pages/ai-powered-insights-and-recommendations just render this
// object — no additional business logic.
//
// Function of behavior
// --------------------
//
// Function-of-behavior inference uses one of two paths in priority order:
//
//   1. If the teacher explicitly set `hypothesizedFunction` on at least
//      half of the incidents, we take the mode ("stated"). This is the
//      correct answer because it's what a trained observer said.
//
//   2. Otherwise, we map antecedents to their most-likely maintaining
//      function using a conservative categorical table ("inferred"). The
//      mapping is drawn from FBA training basics — task demands and
//      non-preferred activities suggest escape; denied access suggests
//      tangible; sensory overload suggests sensory; attention-on-others
//      and alone suggest attention. It's an approximation.
//
// Every inferred function is labelled with a `functionSource` of "stated"
// or "inferred" and a `functionConfidence` of "high" / "medium" / "low"
// so the UI can be honest about the strength of the signal.
// ---------------------------------------------------------------------------

import {
  FUNCTIONS_OF_BEHAVIOR,
  FUNCTION_BY_VALUE,
  ANTECEDENT_OPTIONS,
  LOCATION_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
} from '../pbisConstants';

// ---------- Constants ----------------------------------------------------

// A goal is "stalled" if there's been no progress entry in this many days.
// Two calendar weeks is the boundary where most teachers agree data
// collection has slipped, per casual polling of the IEP-team community.
export const STALLED_GOAL_DAYS = 14;

// A student's IEP review is "due soon" this many days out. The 30-day
// window matches the district-facing compliance guidance most SPED
// coordinators use ("review the packet a month before the meeting").
export const REVIEW_DUE_SOON_DAYS = 30;

// Minimum number of incidents required before we're willing to name a
// pattern (day-of-week concentration, function-of-behavior, etc.).
// Below three, we're just retelling one bad afternoon.
const MIN_INCIDENTS_FOR_PATTERN = 3;

// Minimum number of progress entries required before we compute velocity.
// A single point is a status, not a trend; two is a slope but not a story.
const MIN_ENTRIES_FOR_VELOCITY = 3;

// Behavior-type codes that are safety-critical. Any occurrence in the
// last 7 days bumps the student's signal to "critical" regardless of
// other flags.
const CRITICAL_BEHAVIOR_TYPES = new Set([
  'physical-aggression',
  'self-injury',
  'elopement',
]);

// Antecedent → likely-function mapping used only as a fallback when the
// teacher hasn't set hypothesizedFunction. Conservative — anything not
// in this table lands on 'unknown' and we surface "not enough data" to
// the UI rather than guess.
const ANTECEDENT_TO_FUNCTION = {
  'task-demand': 'escape',
  'non-preferred-activity': 'escape',
  'corrective-feedback': 'escape',
  'physical-prompt': 'escape',
  transition: 'escape',
  'schedule-change': 'escape',
  'denied-access': 'tangible',
  'told-no': 'tangible',
  'attention-to-others': 'attention',
  alone: 'attention',
  'peer-conflict': 'attention',
  waiting: 'attention',
  'sensory-overload': 'sensory',
};

// ---------- Small helpers ------------------------------------------------

const ANTECEDENT_LABEL = new Map(ANTECEDENT_OPTIONS.map((o) => [o.value, o.label]));
const LOCATION_LABEL = new Map(LOCATION_OPTIONS.map((o) => [o.value, o.label]));
const BEHAVIOR_TYPE_LABEL = new Map(BEHAVIOR_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const DAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function isFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function toMs(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function daysBetween(aMs, bMs) {
  if (!isFiniteNumber(aMs) || !isFiniteNumber(bMs)) return null;
  return Math.round((aMs - bMs) / (1000 * 60 * 60 * 24));
}

function withinDays(ms, nowMs, days) {
  if (!isFiniteNumber(ms) || !isFiniteNumber(nowMs)) return false;
  return nowMs - ms <= days * 24 * 60 * 60 * 1000 && nowMs - ms >= 0;
}

function timeBucketFor(ms) {
  if (!isFiniteNumber(ms)) return 'unknown';
  const h = new Date(ms).getHours();
  if (h < 11) return 'morning';
  if (h < 13) return 'lunch';
  if (h < 15) return 'afternoon';
  return 'late';
}

const TIME_BUCKET_LABEL = {
  morning: 'morning (before 11 AM)',
  lunch: 'around lunch (11 AM – 1 PM)',
  afternoon: 'afternoon (1 – 3 PM)',
  late: 'late in the day (after 3 PM)',
  unknown: 'unspecified time',
};

// mode — return the highest-count entry from a { value → count } object.
// Ties broken by first-seen order (Object.entries insertion order is
// stable in modern engines).
function modeOf(counts) {
  let best = null;
  let bestCount = 0;
  for (const [value, count] of Object.entries(counts)) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best ? { value: best, count: bestCount } : null;
}

function tallyBy(items, key) {
  const out = {};
  for (const it of items) {
    const v = it?.[key];
    if (!v) continue;
    out[v] = (out[v] || 0) + 1;
  }
  return out;
}

function tallyByFn(items, fn) {
  const out = {};
  for (const it of items) {
    const v = fn(it);
    if (!v) continue;
    out[v] = (out[v] || 0) + 1;
  }
  return out;
}

function toSortedList(counts, labelFn) {
  return Object.entries(counts)
    .map(([value, count]) => ({ value, count, label: labelFn ? labelFn(value) : value }))
    .sort((a, b) => b.count - a.count);
}

// ---------- Linear regression on progress entries -----------------------
//
// We treat each entry as a point (dayOffset, normalizedValue) where
// normalizedValue is `value / targetValue * 100` when both are set, else
// falls back to whatever `progressPercent` the DB computed. `dayOffset`
// is days-since-the-first-entry, so the intercept is the earliest value
// and the slope is percentage-points-per-day.
//
// Returns { slope, intercept, latest, latestDate, first, firstDate,
//           entries, staleDays, r2, projectionDays, projectionDate } or null.
//
// Projection is only reported when the slope is meaningfully positive
// (>= 0.1 pct/day, i.e. ~0.7 pct/week) AND the goal isn't already at
// 100%; otherwise projectionDays is null and the UI will show "no
// projection yet" instead of an absurd 12-year date.

function computeGoalTrajectory(entries, goal, nowMs) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return {
      count: 0,
      staleDays: null,
      slope: 0,
      latest: null,
      latestDate: null,
      projectionDays: null,
      projectionDate: null,
    };
  }

  const points = entries
    .map((e) => {
      const ts = toMs(e.observedOn);
      if (!ts) return null;
      let value = null;
      if (isFiniteNumber(e.value) && isFiniteNumber(e.targetValue) && e.targetValue > 0) {
        value = (e.value / e.targetValue) * 100;
      } else if (isFiniteNumber(e.value) && isFiniteNumber(goal?.targetValue) && goal.targetValue > 0) {
        value = (e.value / goal.targetValue) * 100;
      } else if (isFiniteNumber(e.value)) {
        // The teacher entered raw values with no target. We can still
        // fit a line to them if they look like a percentage already
        // (0–100 range); anything else we ignore.
        if (e.value >= 0 && e.value <= 100) value = e.value;
      }
      if (value == null) return null;
      return { ts, value: Math.max(0, Math.min(100, value)) };
    })
    .filter(Boolean)
    .sort((a, b) => a.ts - b.ts);

  if (points.length === 0) {
    return {
      count: 0,
      staleDays: null,
      slope: 0,
      latest: null,
      latestDate: null,
      projectionDays: null,
      projectionDate: null,
    };
  }

  const first = points[0];
  const latest = points[points.length - 1];
  const staleDays = daysBetween(nowMs, latest.ts);

  // If we can't fit a line (< 3 points, or all on the same day), still
  // return the most-recent value so the UI can show progress. Trend
  // stays null.
  if (points.length < MIN_ENTRIES_FOR_VELOCITY) {
    return {
      count: points.length,
      staleDays,
      slope: 0,
      latest: latest.value,
      latestDate: new Date(latest.ts).toISOString(),
      first: first.value,
      firstDate: new Date(first.ts).toISOString(),
      r2: null,
      projectionDays: null,
      projectionDate: null,
    };
  }

  const t0 = first.ts;
  const xs = points.map((p) => (p.ts - t0) / (1000 * 60 * 60 * 24));
  const ys = points.map((p) => p.value);
  const n = xs.length;
  const xMean = xs.reduce((a, b) => a + b, 0) / n;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i += 1) {
    num += (xs[i] - xMean) * (ys[i] - yMean);
    den += (xs[i] - xMean) ** 2;
    ssTot += (ys[i] - yMean) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = yMean - slope * xMean;

  // R² for the fit — used only for display ("good fit" vs "noisy").
  let ssRes = 0;
  for (let i = 0; i < n; i += 1) {
    const predicted = intercept + slope * xs[i];
    ssRes += (ys[i] - predicted) ** 2;
  }
  const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  // Projection: days from the latest entry until the fitted line crosses
  // the mastery threshold. Default to 80 (% mastery) unless the goal has
  // an explicit mastery threshold in `masteryCriteria` we could parse —
  // which we don't try to do here; 80 is the standard.
  const target = 80;
  let projectionDays = null;
  let projectionDate = null;
  if (slope >= 0.1 && latest.value < target) {
    const daysFromLatest = (target - latest.value) / slope;
    if (Number.isFinite(daysFromLatest) && daysFromLatest > 0 && daysFromLatest < 365 * 3) {
      projectionDays = Math.round(daysFromLatest);
      projectionDate = new Date(latest.ts + daysFromLatest * 24 * 60 * 60 * 1000).toISOString();
    }
  }

  return {
    count: n,
    staleDays,
    slope,
    slopePerWeek: slope * 7,
    latest: latest.value,
    latestDate: new Date(latest.ts).toISOString(),
    first: first.value,
    firstDate: new Date(first.ts).toISOString(),
    r2,
    projectionDays,
    projectionDate,
  };
}

// ---------- Per-goal analysis -------------------------------------------

function analyzeGoal(goal, entries, nowMs) {
  const traj = computeGoalTrajectory(entries, goal, nowMs);
  const staleDays = traj.staleDays;
  const isStalled = staleDays != null && staleDays >= STALLED_GOAL_DAYS;
  const isMastered = goal?.status === 'mastered' || (traj.latest != null && traj.latest >= 100);
  const isRegressing = traj.slopePerWeek != null && traj.slopePerWeek < -0.5;
  const isAhead = traj.slopePerWeek != null && traj.slopePerWeek >= 1.5;

  let trend = 'flat';
  if (isMastered) trend = 'mastered';
  else if (isRegressing) trend = 'regressing';
  else if (traj.slopePerWeek >= 0.5) trend = 'improving';
  else if (traj.slopePerWeek <= -0.1) trend = 'declining';

  return {
    goalId: goal?.id,
    area: goal?.goalArea || 'other',
    specific: goal?.specific || '',
    status: goal?.status || 'active',
    latest: traj.latest,
    firstDate: traj.firstDate,
    latestDate: traj.latestDate,
    slopePerWeek: traj.slopePerWeek ?? null,
    staleDays,
    isStalled,
    isMastered,
    isRegressing,
    isAhead,
    trend,
    projectionDays: traj.projectionDays,
    projectionDate: traj.projectionDate,
    entryCount: traj.count,
    r2: traj.r2,
  };
}

// ---------- Per-student analysis ----------------------------------------

function inferFunctionOfBehavior(incidents) {
  // Path 1: the teacher stated a function on at least half of the incidents.
  const stated = incidents
    .map((i) => (i.hypothesizedFunction || '').toLowerCase())
    .filter((v) => v && v !== 'unknown');
  if (stated.length >= Math.max(2, incidents.length / 2)) {
    const counts = {};
    for (const v of stated) counts[v] = (counts[v] || 0) + 1;
    const m = modeOf(counts);
    if (m) {
      const share = m.count / stated.length;
      return {
        value: m.value,
        source: 'stated',
        confidence: share >= 0.75 ? 'high' : share >= 0.5 ? 'medium' : 'low',
        sampleSize: stated.length,
        share,
      };
    }
  }

  // Path 2: fall back to antecedent → likely function.
  const inferred = incidents
    .map((i) => ANTECEDENT_TO_FUNCTION[(i.antecedent || '').toLowerCase()] || null)
    .filter(Boolean);
  if (inferred.length < MIN_INCIDENTS_FOR_PATTERN) {
    return { value: 'unknown', source: 'inferred', confidence: 'low', sampleSize: inferred.length, share: 0 };
  }
  const counts = {};
  for (const v of inferred) counts[v] = (counts[v] || 0) + 1;
  const m = modeOf(counts);
  if (!m) {
    return { value: 'unknown', source: 'inferred', confidence: 'low', sampleSize: inferred.length, share: 0 };
  }
  const share = m.count / inferred.length;
  return {
    value: m.value,
    source: 'inferred',
    confidence: share >= 0.7 ? 'medium' : 'low',
    sampleSize: inferred.length,
    share,
  };
}

function analyzeBehavior(incidents, nowMs) {
  const weekMs = 7 * 24 * 60 * 60 * 1000;

  const thisWeek = incidents.filter((i) => nowMs - (toMs(i.occurredAt) || 0) <= weekMs);
  const lastWeek = incidents.filter((i) => {
    const t = toMs(i.occurredAt);
    if (!t) return false;
    return nowMs - t <= 2 * weekMs && nowMs - t > weekMs;
  });

  const intensities = incidents.map((i) => i.intensity).filter(isFiniteNumber);
  const avgIntensity = intensities.length
    ? intensities.reduce((a, b) => a + b, 0) / intensities.length
    : null;

  const dayCounts = tallyByFn(incidents, (i) => {
    const t = toMs(i.occurredAt);
    if (!t) return null;
    return DAY_LABEL[new Date(t).getDay()];
  });
  const locationCounts = tallyBy(incidents, 'location');
  const antecedentCounts = tallyBy(incidents, 'antecedent');
  const timeCounts = tallyByFn(incidents, (i) => timeBucketFor(toMs(i.occurredAt)));
  const behaviorTypeCounts = tallyBy(incidents, 'behaviorType');

  const topDay = modeOf(dayCounts);
  const topLocation = modeOf(locationCounts);
  const topAntecedent = modeOf(antecedentCounts);
  const topTime = modeOf(timeCounts);

  const fn = incidents.length >= MIN_INCIDENTS_FOR_PATTERN
    ? inferFunctionOfBehavior(incidents)
    : { value: 'unknown', source: 'inferred', confidence: 'low', sampleSize: incidents.length, share: 0 };

  const hasCriticalRecent = thisWeek.some(
    (i) => CRITICAL_BEHAVIOR_TYPES.has(i.behaviorType) || (isFiniteNumber(i.intensity) && i.intensity >= 4),
  );

  // Compose a one-sentence pattern headline when we have enough data.
  let patternHeadline = null;
  if (incidents.length >= MIN_INCIDENTS_FOR_PATTERN) {
    const bits = [];
    if (topTime && topTime.count >= Math.max(3, incidents.length * 0.4)) {
      bits.push(`in the ${TIME_BUCKET_LABEL[topTime.value]}`);
    }
    if (topLocation && topLocation.count >= Math.max(3, incidents.length * 0.4)) {
      bits.push(`in the ${LOCATION_LABEL.get(topLocation.value) || topLocation.value}`);
    }
    if (topAntecedent && topAntecedent.count >= Math.max(3, incidents.length * 0.4)) {
      const label = ANTECEDENT_LABEL.get(topAntecedent.value) || topAntecedent.value;
      bits.push(`after "${label.toLowerCase()}"`);
    }
    if (bits.length) {
      patternHeadline = `Most incidents happen ${bits.join(', ')}.`;
    }
  }

  return {
    total: incidents.length,
    thisWeek: thisWeek.length,
    lastWeek: lastWeek.length,
    weekOverWeekDelta: thisWeek.length - lastWeek.length,
    avgIntensity,
    byDayOfWeek: dayCounts,
    byLocation: toSortedList(locationCounts, (v) => LOCATION_LABEL.get(v) || v),
    byAntecedent: toSortedList(antecedentCounts, (v) => ANTECEDENT_LABEL.get(v) || v),
    byTimeBucket: toSortedList(timeCounts, (v) => TIME_BUCKET_LABEL[v] || v),
    byBehaviorType: toSortedList(behaviorTypeCounts, (v) => BEHAVIOR_TYPE_LABEL.get(v) || v),
    topDayOfWeek: topDay ? { value: topDay.value, count: topDay.count, label: DAY_LONG[DAY_LABEL.indexOf(topDay.value)] } : null,
    topLocation: topLocation ? { value: topLocation.value, count: topLocation.count, label: LOCATION_LABEL.get(topLocation.value) || topLocation.value } : null,
    topAntecedent: topAntecedent ? { value: topAntecedent.value, count: topAntecedent.count, label: ANTECEDENT_LABEL.get(topAntecedent.value) || topAntecedent.value } : null,
    topTimeBucket: topTime ? { value: topTime.value, count: topTime.count, label: TIME_BUCKET_LABEL[topTime.value] || topTime.value } : null,
    likelyFunction: fn,
    hasCriticalRecent,
    patternHeadline,
  };
}

function reviewStatus(student, nowMs) {
  const due = student?.iepAnnualReviewDue;
  if (!due) return { hasDate: false, dueDate: null, daysLeft: null, overdue: false, dueSoon: false };
  const dueMs = toMs(due) ?? toMs(`${due}T00:00:00`);
  if (!dueMs) return { hasDate: false, dueDate: due, daysLeft: null, overdue: false, dueSoon: false };
  const daysLeft = Math.round((dueMs - nowMs) / (1000 * 60 * 60 * 24));
  return {
    hasDate: true,
    dueDate: due,
    daysLeft,
    overdue: daysLeft < 0,
    dueSoon: daysLeft >= 0 && daysLeft <= REVIEW_DUE_SOON_DAYS,
  };
}

function analyzeStudent(student, { goals, incidents, progressEntries, nowMs }) {
  const studentGoals = goals.filter((g) => g.studentId === student.id);
  const studentIncidents = incidents.filter((i) => i.studentId === student.id);
  const studentEntriesByGoal = new Map();
  for (const e of progressEntries) {
    if (!studentEntriesByGoal.has(e.goalId)) studentEntriesByGoal.set(e.goalId, []);
    studentEntriesByGoal.get(e.goalId).push(e);
  }

  const analyzedGoals = studentGoals.map((g) => analyzeGoal(g, studentEntriesByGoal.get(g.id) || [], nowMs));
  const behavior = analyzeBehavior(studentIncidents, nowMs);
  const review = reviewStatus(student, nowMs);

  // Flags — short codes the UI can filter/sort/color-code on.
  const flags = [];
  if (review.overdue) flags.push('review_overdue');
  else if (review.dueSoon) flags.push('review_due_soon');
  if (analyzedGoals.some((g) => g.isStalled && !g.isMastered)) flags.push('stalled_goal');
  if (analyzedGoals.some((g) => g.isRegressing)) flags.push('regressing_goal');
  if (analyzedGoals.some((g) => g.isAhead)) flags.push('ahead_of_pace');
  if (behavior.hasCriticalRecent) flags.push('critical_behavior');
  if (behavior.weekOverWeekDelta >= 3) flags.push('behavior_spike');
  if (behavior.weekOverWeekDelta <= -3) flags.push('behavior_drop');
  if (studentGoals.length > 0 && analyzedGoals.every((g) => g.entryCount === 0)) flags.push('no_data_yet');
  if (behavior.patternHeadline) flags.push('behavior_pattern');
  if (behavior.likelyFunction?.value && behavior.likelyFunction.value !== 'unknown' && behavior.likelyFunction.confidence !== 'low') {
    flags.push('function_identified');
  }

  // Signal — a coarse priority for UI grouping.
  //   critical  → user needs to look at this student now
  //   attention → soon; not urgent
  //   strong    → things are going well
  //   quiet     → not enough data to say
  let signal = 'quiet';
  if (flags.includes('critical_behavior') || flags.includes('review_overdue')) signal = 'critical';
  else if (
    flags.includes('regressing_goal') ||
    flags.includes('stalled_goal') ||
    flags.includes('review_due_soon') ||
    flags.includes('behavior_spike')
  )
    signal = 'attention';
  else if (
    flags.includes('ahead_of_pace') &&
    !flags.includes('critical_behavior') &&
    !flags.includes('behavior_spike')
  )
    signal = 'strong';
  else if (studentGoals.length > 0 || studentIncidents.length > 0) signal = 'steady';

  // Highlights — up to three plain-language bullet points the UI can
  // show on the student's insight card.
  const highlights = [];
  if (review.overdue) highlights.push(`IEP review is ${Math.abs(review.daysLeft)} days overdue.`);
  else if (review.dueSoon) highlights.push(`IEP review is due in ${review.daysLeft} days.`);

  const stalled = analyzedGoals.find((g) => g.isStalled && !g.isMastered);
  if (stalled) highlights.push(`No data on the "${stalled.area}" goal in ${stalled.staleDays} days.`);

  const regressing = analyzedGoals.find((g) => g.isRegressing);
  if (regressing) {
    highlights.push(
      `"${regressing.area}" goal is trending down (${regressing.slopePerWeek.toFixed(1)} pts/wk).`,
    );
  }

  const ahead = analyzedGoals.find((g) => g.isAhead && g.projectionDate);
  if (ahead) {
    const dt = new Date(ahead.projectionDate);
    const label = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    highlights.push(`On track to master "${ahead.area}" by ${label}.`);
  }

  if (behavior.patternHeadline && highlights.length < 3) {
    highlights.push(behavior.patternHeadline);
  }

  if (behavior.likelyFunction.value !== 'unknown' && behavior.likelyFunction.confidence !== 'low' && highlights.length < 3) {
    const label = FUNCTION_BY_VALUE[behavior.likelyFunction.value]?.label || behavior.likelyFunction.value;
    const kind = behavior.likelyFunction.source === 'stated' ? 'stated' : 'likely';
    highlights.push(
      `Function of behavior looks ${kind === 'stated' ? '' : 'like '}${label} (${behavior.likelyFunction.sampleSize} incidents).`,
    );
  }

  return {
    studentId: student.id,
    studentName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unnamed student',
    grade: student.grade || null,
    primaryDisability: student.primaryDisability || null,
    signal,
    flags,
    review,
    goals: analyzedGoals,
    behavior,
    highlights,
    activeGoalCount: analyzedGoals.filter((g) => !g.isMastered).length,
    masteredGoalCount: analyzedGoals.filter((g) => g.isMastered).length,
    incidentCount: studentIncidents.length,
  };
}

// ---------- Caseload roll-ups -------------------------------------------

function summarize(studentInsights, { students, goals, incidents, nowMs }) {
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const thisWeek = incidents.filter((i) => nowMs - (toMs(i.occurredAt) || 0) <= weekMs);
  const lastWeek = incidents.filter((i) => {
    const t = toMs(i.occurredAt);
    if (!t) return false;
    return nowMs - t <= 2 * weekMs && nowMs - t > weekMs;
  });
  const allGoals = studentInsights.flatMap((s) => s.goals);
  const active = allGoals.filter((g) => !g.isMastered);

  return {
    totalStudents: students.length,
    studentsBySignal: {
      critical: studentInsights.filter((s) => s.signal === 'critical').length,
      attention: studentInsights.filter((s) => s.signal === 'attention').length,
      strong: studentInsights.filter((s) => s.signal === 'strong').length,
      steady: studentInsights.filter((s) => s.signal === 'steady').length,
      quiet: studentInsights.filter((s) => s.signal === 'quiet').length,
    },
    totalGoals: goals.length,
    activeGoals: active.length,
    goalsAhead: allGoals.filter((g) => g.isAhead).length,
    goalsImproving: allGoals.filter((g) => g.trend === 'improving').length,
    goalsFlat: active.filter((g) => g.trend === 'flat').length,
    goalsRegressing: allGoals.filter((g) => g.isRegressing).length,
    goalsStalled: active.filter((g) => g.isStalled).length,
    goalsMastered: allGoals.filter((g) => g.isMastered).length,
    incidentsThisWeek: thisWeek.length,
    incidentsLastWeek: lastWeek.length,
    incidentsPctChange:
      lastWeek.length === 0
        ? thisWeek.length === 0
          ? 0
          : null
        : Math.round(((thisWeek.length - lastWeek.length) / lastWeek.length) * 100),
    reviewsOverdue: studentInsights.filter((s) => s.review.overdue).length,
    reviewsDueSoon: studentInsights.filter((s) => s.review.dueSoon).length,
  };
}

// ---------- Caseload patterns -------------------------------------------
//
// Cross-student signals that a teacher would want on their radar. Each
// pattern has its own evidence count so the UI can rank them by strength.

function detectCaseloadPatterns(studentInsights, { nowMs }) {
  const patterns = [];

  // Behavior — students with the same function of behavior identified.
  const byFunction = new Map();
  for (const s of studentInsights) {
    const fv = s.behavior.likelyFunction?.value;
    if (!fv || fv === 'unknown' || s.behavior.likelyFunction.confidence === 'low') continue;
    if (!byFunction.has(fv)) byFunction.set(fv, []);
    byFunction.get(fv).push(s);
  }
  for (const [fv, group] of byFunction.entries()) {
    if (group.length < 2) continue;
    const fnMeta = FUNCTION_BY_VALUE[fv];
    patterns.push({
      id: `fn-${fv}`,
      kind: 'function_cluster',
      headline: `${group.length} students share ${fnMeta?.label || fv} function of behavior`,
      detail: `These students' incidents share a common maintaining function. Group-level supports (small-group instruction, shared visuals) may help all of them.`,
      studentIds: group.map((s) => s.studentId),
      evidenceCount: group.reduce((n, s) => n + s.behavior.total, 0),
      icon: fnMeta?.icon || 'Users',
    });
  }

  // Stalled goals across the caseload.
  const stalledStudents = studentInsights.filter((s) => s.flags.includes('stalled_goal'));
  if (stalledStudents.length >= 1) {
    patterns.push({
      id: 'stalled-goals',
      kind: 'stalled_goals',
      headline: `${stalledStudents.length} student${stalledStudents.length === 1 ? '' : 's'} ${stalledStudents.length === 1 ? 'has' : 'have'} at least one stalled goal`,
      detail: `No data has been logged on these goals in ${STALLED_GOAL_DAYS}+ days. Progress-monitoring frequency drives IEP-progress reporting; catching up here also protects compliance.`,
      studentIds: stalledStudents.map((s) => s.studentId),
      evidenceCount: stalledStudents.reduce(
        (n, s) => n + s.goals.filter((g) => g.isStalled && !g.isMastered).length,
        0,
      ),
      icon: 'Clock',
    });
  }

  // Regressing goals.
  const regressing = studentInsights.filter((s) => s.flags.includes('regressing_goal'));
  if (regressing.length >= 1) {
    patterns.push({
      id: 'regressing-goals',
      kind: 'regressing_goals',
      headline: `${regressing.length} student${regressing.length === 1 ? '' : 's'} showing a downward trend on at least one goal`,
      detail: `Data is coming in, but the fitted line is trending down. Consider revisiting instructional strategy, prerequisite skills, or environmental changes.`,
      studentIds: regressing.map((s) => s.studentId),
      evidenceCount: regressing.length,
      icon: 'TrendingDown',
    });
  }

  // Compliance risk — reviews due soon or overdue.
  const reviewRisk = studentInsights.filter((s) => s.review.overdue || s.review.dueSoon);
  if (reviewRisk.length >= 1) {
    const overdue = reviewRisk.filter((s) => s.review.overdue).length;
    patterns.push({
      id: 'review-risk',
      kind: 'compliance',
      headline: overdue > 0
        ? `${overdue} IEP review${overdue === 1 ? '' : 's'} overdue, ${reviewRisk.length - overdue} due soon`
        : `${reviewRisk.length} IEP review${reviewRisk.length === 1 ? '' : 's'} coming up in the next ${REVIEW_DUE_SOON_DAYS} days`,
      detail: `Federal IDEA requires annual review meetings. Start pulling packets a month out to stay ahead of the deadline.`,
      studentIds: reviewRisk.map((s) => s.studentId),
      evidenceCount: reviewRisk.length,
      icon: 'CalendarClock',
    });
  }

  // Positive momentum.
  const ahead = studentInsights.filter((s) => s.flags.includes('ahead_of_pace'));
  if (ahead.length >= 1) {
    patterns.push({
      id: 'ahead-of-pace',
      kind: 'ahead',
      headline: `${ahead.length} student${ahead.length === 1 ? '' : 's'} on track to master a goal early`,
      detail: `Current velocity would hit the 80% mastery threshold before the target date. Consider drafting the next-level goal now.`,
      studentIds: ahead.map((s) => s.studentId),
      evidenceCount: ahead.length,
      icon: 'TrendingUp',
    });
  }

  // Critical safety — physical-aggression / self-injury / elopement / intensity 4+ this week.
  const critical = studentInsights.filter((s) => s.behavior.hasCriticalRecent);
  if (critical.length >= 1) {
    patterns.push({
      id: 'critical-behavior',
      kind: 'critical_behavior',
      headline: `${critical.length} student${critical.length === 1 ? '' : 's'} with high-severity behavior this week`,
      detail: `Aggression, self-injury, elopement, or intensity-4+ incidents in the past 7 days. Review the safety-response plan and current BIP.`,
      studentIds: critical.map((s) => s.studentId),
      evidenceCount: critical.reduce((n, s) => n + s.behavior.thisWeek, 0),
      icon: 'AlertTriangle',
    });
  }

  return patterns.sort((a, b) => {
    // Sort by "how urgent should the teacher care": safety first, then
    // compliance, then stalled/regressing, then patterns, then positive.
    const priority = {
      critical_behavior: 5,
      compliance: 4,
      regressing_goals: 3,
      stalled_goals: 3,
      function_cluster: 2,
      ahead: 1,
    };
    return (priority[b.kind] || 0) - (priority[a.kind] || 0);
  });
}

// ---------- Deterministic recommendations -------------------------------
//
// Each recommendation is CTA-shaped: headline + detail + a button that
// takes the teacher somewhere real in the app. No AI hallucination —
// these are consequences of the deterministic patterns above.

function buildRecommendations(studentInsights, patterns) {
  const recs = [];

  // Any student with a stalled goal → CTA to log data for them.
  const stalled = studentInsights.filter((s) => s.flags.includes('stalled_goal'));
  for (const s of stalled) {
    const worst = s.goals
      .filter((g) => g.isStalled && !g.isMastered)
      .sort((a, b) => (b.staleDays || 0) - (a.staleDays || 0))[0];
    if (!worst) continue;
    recs.push({
      id: `log-${s.studentId}-${worst.goalId}`,
      kind: 'log_data',
      studentIds: [s.studentId],
      headline: `Log a data point for ${s.studentName}`,
      detail: `The "${worst.area}" goal hasn't been updated in ${worst.staleDays} days. A single data point restarts the trend line.`,
      ctaLabel: 'Log data now',
      ctaTo: '/iep-goals-and-objectives-tracking',
      ctaState: { openCollectData: true, prefStudentId: s.studentId, prefKind: 'iep' },
      icon: 'ClipboardCheck',
      priority: 'medium',
    });
  }

  // Any student with a regressing goal → CTA to open the goal detail.
  const regressing = studentInsights.filter((s) => s.flags.includes('regressing_goal'));
  for (const s of regressing) {
    const worst = s.goals
      .filter((g) => g.isRegressing)
      .sort((a, b) => (a.slopePerWeek || 0) - (b.slopePerWeek || 0))[0];
    if (!worst) continue;
    recs.push({
      id: `review-${s.studentId}-${worst.goalId}`,
      kind: 'review_goal',
      studentIds: [s.studentId],
      headline: `Review the "${worst.area}" goal for ${s.studentName}`,
      detail: `Progress is trending down at ${Math.abs(worst.slopePerWeek).toFixed(1)} pts/week. Consider revisiting the intervention plan or breaking the goal into subskills.`,
      ctaLabel: 'Open goal detail',
      ctaTo: '/iep-goals-and-objectives-tracking',
      ctaState: { openGoalId: worst.goalId },
      icon: 'AlertOctagon',
      priority: 'high',
    });
  }

  // Any review overdue → CTA to open student profile.
  const overdue = studentInsights.filter((s) => s.review.overdue);
  for (const s of overdue) {
    recs.push({
      id: `review-overdue-${s.studentId}`,
      kind: 'compliance',
      studentIds: [s.studentId],
      headline: `${s.studentName}'s annual review is ${Math.abs(s.review.daysLeft)} days overdue`,
      detail: `IDEA requires an annual review. Open the profile, update the date, and print the one-pager for the team meeting.`,
      ctaLabel: 'Open student profile',
      ctaTo: '/student-profile-management',
      ctaState: { studentId: s.studentId },
      icon: 'CalendarX',
      priority: 'high',
    });
  }

  // Ahead-of-pace → suggest drafting the next goal.
  const ahead = studentInsights.filter((s) => s.flags.includes('ahead_of_pace'));
  for (const s of ahead) {
    recs.push({
      id: `next-goal-${s.studentId}`,
      kind: 'draft_next_goal',
      studentIds: [s.studentId],
      headline: `${s.studentName} is on track to master a goal early`,
      detail: `Current velocity hits mastery ahead of schedule. Draft the next-level goal now so the team can approve it at the next meeting.`,
      ctaLabel: 'Draft next goal',
      ctaTo: '/iep-goals-and-objectives-tracking',
      ctaState: { openNewGoal: true, prefStudentId: s.studentId },
      icon: 'Sparkles',
      priority: 'low',
    });
  }

  // Function-of-behavior cluster → suggest a small-group intervention.
  const fnPattern = patterns.find((p) => p.kind === 'function_cluster');
  if (fnPattern && fnPattern.studentIds.length >= 2) {
    recs.push({
      id: fnPattern.id,
      kind: 'group_intervention',
      studentIds: fnPattern.studentIds,
      headline: `Consider a small-group support for ${fnPattern.studentIds.length} students`,
      detail: fnPattern.detail,
      ctaLabel: 'Open Team Hub',
      ctaTo: '/multi-user-collaboration-hub',
      ctaState: null,
      icon: 'Users',
      priority: 'medium',
    });
  }

  // Rank recs — high priority first, then medium, then low. Stable sort.
  const order = { high: 3, medium: 2, low: 1 };
  return recs
    .map((r, idx) => ({ ...r, __idx: idx }))
    .sort((a, b) => (order[b.priority] - order[a.priority]) || (a.__idx - b.__idx))
    .map(({ __idx, ...r }) => r);
}

// ---------- Public entrypoint -------------------------------------------

export function analyzeCaseload({
  students = [],
  goals = [],
  incidents = [],
  progressEntries = [],
  now,
} = {}) {
  const nowMs = now ? new Date(now).getTime() : Date.now();
  const studentInsights = students.map((s) =>
    analyzeStudent(s, { goals, incidents, progressEntries, nowMs }),
  );
  const summary = summarize(studentInsights, { students, goals, incidents, nowMs });
  const patterns = detectCaseloadPatterns(studentInsights, { nowMs });
  const recommendations = buildRecommendations(studentInsights, patterns);
  return {
    summary,
    studentInsights,
    patterns,
    recommendations,
    computedAt: new Date(nowMs).toISOString(),
  };
}

// ---------- Building blocks for UI-only callers -------------------------

export {
  analyzeStudent,
  analyzeGoal,
  analyzeBehavior,
  inferFunctionOfBehavior,
  computeGoalTrajectory,
  reviewStatus,
  FUNCTIONS_OF_BEHAVIOR,
  TIME_BUCKET_LABEL,
};
